import React, { useState, useEffect, useCallback, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { Loader, Receipt, Eye, Download } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

const formatDate = (dateString) => {
  const d = new Date(dateString);
  return [
    String(d.getDate()).padStart(2, "0"),
    String(d.getMonth() + 1).padStart(2, "0"),
    d.getFullYear(),
  ].join(".");
};

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

/* Shell commun (même structure que Facility VIP) */
const Shell = ({ title, onBack, right, children }) => (
  <div className="fv-modal">
    <header className="fv-header">
      <button type="button" className="fv-back" aria-label="Back" onClick={onBack} />
      <h3 className="fv-title">{title}</h3>
      {right ?? <span className="fv-header-spacer" />}
    </header>
    <div className="fv-body">{children}</div>
  </div>
);

const ClientInvoicesPayed = ({ onClose }) => {
  const [invoices, setInvoices] = useState([]);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [rect, setRect] = useState(null);
  const navigate = useNavigate();

  /* ── Zone du dashboard pour limiter le flou ── */
  useLayoutEffect(() => {
    const measure = () => {
      const el = document.querySelector(".overlayClient");
      if (el) {
        const r = el.getBoundingClientRect();
        setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
      } else {
        setRect(null);
      }
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  /* ── Auth ── */
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) { navigate("/login"); return; }
    try {
      const decoded = jwtDecode(token);
      if (decoded.exp < Math.floor(Date.now() / 1000)) {
        localStorage.removeItem("token");
        navigate("/login");
        return;
      }
      setCurrentUserId(decoded.id);
    } catch {
      navigate("/login");
    }
  }, [navigate]);

  /* ── Fetch paid invoices ── */
  const fetchInvoices = useCallback(async () => {
    if (!currentUserId) return;
    setIsLoading(true);
    try {
      const { data } = await axios.get(
        `${API_URL}/invoices/user/${currentUserId}`,
        { headers: authHeaders() }
      );
      setInvoices(
        (data.invoices || [])
          .filter((inv) => inv.status === "paid")
          .sort((a, b) => new Date(b.invoiceDate) - new Date(a.invoiceDate))
      );
      setError(null);
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to fetch paid invoices.";
      setError(msg);
      toast.error(msg);
      setInvoices([]);
    } finally {
      setIsLoading(false);
    }
  }, [currentUserId]);

  useEffect(() => { fetchInvoices(); }, [fetchInvoices]);

  /* ── View PDF ── */
  const handleViewInvoice = async (invoiceId) => {
    try {
      const res = await axios.get(`${API_URL}/invoices/view/${invoiceId}`, {
        headers: authHeaders(),
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(
        new Blob([res.data], { type: "application/pdf" })
      );
      window.open(url, "_blank");
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (err) {
      toast.error(err.response?.data?.message || "Unable to open invoice.");
    }
  };

  /* ── Download PDF ── */
  const handleDownloadInvoice = async (invoiceId) => {
    try {
      const res = await axios.get(`${API_URL}/invoices/download/${invoiceId}`, {
        headers: authHeaders(),
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(
        new Blob([res.data], { type: "application/pdf" })
      );
      const link = document.createElement("a");
      link.href = url;
      link.download = `invoice-${invoiceId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.response?.data?.message || "Unable to download invoice.");
    }
  };

  /* ── Render ── */
  return createPortal(
    <>
     <div
  className="fv-backdrop"
  style={{ borderRadius: 0 }}
  onClick={onClose}
/>

      <Shell
        title="Paid Invoices"
        onBack={onClose}
        right={<span className="fv-count">{invoices.length}</span>}
      >
        {error && <div className="fv-error">{error}</div>}

        {!error && isLoading ? (
          <div className="fv-state">
            <Loader className="fv-spin" size={16} /> Loading…
          </div>
        ) : !error && invoices.length > 0 ? (
          <div className="fv-reports">
            {invoices.map((inv, i) => (
              <div key={inv._id || i} className="fv-report fv-invoice">
                <span className="fv-report-icon">
                  <Receipt size={15} />
                </span>
                <span className="fv-report-text">
                  <strong>{formatDate(inv.invoiceDate)}</strong>
                  <small className="fv-paid">Paid</small>
                </span>

                <button
                  type="button"
                  className="fv-icon-btn"
                  title="View"
                  onClick={() => handleViewInvoice(inv._id)}
                >
                  <Eye size={14} />
                </button>
                <button
                  type="button"
                  className="fv-icon-btn"
                  title="Download"
                  onClick={() => handleDownloadInvoice(inv._id)}
                >
                  <Download size={14} />
                </button>
              </div>
            ))}
          </div>
        ) : (
          !error && <div className="fv-state">No paid invoices found.</div>
        )}
      </Shell>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        style={{ zIndex: 10050 }}
      />
    </>,
    document.body
  );
};

export default ClientInvoicesPayed;